import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react';
import { AppShell } from '@/components/layout/AppShell';
import { LandingPage } from '@/components/landing/LandingPage';
import { DashboardView } from '@/components/modules/dashboard/DashboardView';
import { ReferralsView } from '@/components/modules/referrals/ReferralsView';
import { LeadsView } from '@/components/modules/leads/LeadsView';
import { PayoutsView } from '@/components/modules/payouts/PayoutsView';
import { SupportView } from '@/components/modules/support/SupportView';
import { AnalyticsView } from '@/components/modules/analytics/AnalyticsView';
import { SettingsLayout } from '@/components/modules/settings/SettingsLayout';
import { SettingsProfileSection } from '@/components/modules/settings/SettingsProfileSection';
import { SettingsOrganizationSection } from '@/components/modules/settings/SettingsOrganizationSection';
import { SettingsSecuritySection } from '@/components/modules/settings/SettingsSecuritySection';
import { SettingsTeamSection } from '@/components/modules/settings/SettingsTeamSection';
import { SettingsKycSection } from '@/components/modules/settings/SettingsKycSection';

// Headless Zero-Branding Auth Pages
import { SignInPage } from '@/components/auth/SignInPage';
import { SignUpPage } from '@/components/auth/SignUpPage';
import { ForgotPasswordPage } from '@/components/auth/ForgotPasswordPage';
import { SSOCallback } from '@/components/auth/SSOCallback';

const CLERK_PUBLISHABLE_KEY =
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ||
  'pk_test_bmVhdC1icmVhbS02NDA5LmNsZXJrLmFjY291bnRzLmRldiQ';

export const App: React.FC = () => {
  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      appearance={{
        baseTheme: undefined,
        variables: {
          colorPrimary: '#6366f1',
          colorBackground: '#09090b',
          colorText: '#fafafa',
          colorInputBackground: '#18181b',
          colorInputText: '#fafafa',
          borderRadius: '0.75rem',
        },
      }}
    >
      <BrowserRouter>
        <Routes>
          {/* Public Custom Authentication Routes */}
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/sso-callback" element={<SSOCallback />} />

          {/* Authenticated Workspace & Unauthenticated Landing */}
          <Route
            path="/*"
            element={
              <>
                <SignedIn>
                  <AppShell />
                </SignedIn>
                <SignedOut>
                  <LandingPage />
                </SignedOut>
              </>
            }
          >
            <Route index element={<DashboardView />} />
            <Route path="referrals" element={<ReferralsView />} />
            <Route path="leads" element={<LeadsView />} />
            <Route path="payouts" element={<PayoutsView />} />
            <Route path="settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile" element={<SettingsProfileSection />} />
              <Route path="organization" element={<SettingsOrganizationSection />} />
              <Route path="security" element={<SettingsSecuritySection />} />
              <Route path="team" element={<SettingsTeamSection />} />
              <Route path="kyc" element={<SettingsKycSection />} />
            </Route>
            <Route path="team" element={<Navigate to="/settings/team" replace />} />
            <Route path="kyc" element={<Navigate to="/settings/kyc" replace />} />
            <Route path="support" element={<SupportView />} />
            <Route path="analytics" element={<AnalyticsView />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ClerkProvider>
  );
};

export default App;
